/**
 * A anamnese que o trabalhador responde sozinho — pelo link do celular ou num tablet na recepção.
 *
 * Três passos: confirma o CPF (só então o questionário aparece), responde as perguntas e assina a
 * declaração de veracidade. Enviado, o link acaba: a página não mostra de volta o que foi
 * respondido, porque é dado de saúde e o link pode ficar aberto num aparelho compartilhado.
 *
 * `?tablet=1` é o que a recepção abre: muda o texto final ("devolva o tablet") e marca a origem.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';

import { QuadroDeAssinatura } from '../../components/assinatura/QuadroDeAssinatura';
import anamneseDigitalService, {
  type Pergunta,
  type QuestionarioPublico,
  type Respostas,
  type ResumoPublico,
} from '../../services/anamneseDigitalService';

function formatarCpf(valor: string): string {
  const d = valor.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d{1,2})$/, '.$1-$2');
}

function dataBr(valor: string): string {
  const [ano, mes, dia] = valor.slice(0, 10).split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : valor;
}

const Moldura: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 2 } }}>
    <Paper sx={{ maxWidth: 680, mx: 'auto', p: { xs: 2.5, sm: 4 } }}>{children}</Paper>
  </Box>
);

const CampoDaPergunta: React.FC<{
  pergunta: Pergunta;
  resposta: Respostas[string] | undefined;
  onChange: (valor: string, detalhe?: string) => void;
}> = ({ pergunta, resposta, onChange }) => {
  if (pergunta.tipo === 'texto') {
    return (
      <TextField
        label={pergunta.texto}
        value={resposta?.valor ?? ''}
        onChange={(e) => onChange(e.target.value)}
        multiline
        minRows={2}
        fullWidth
        size="small"
      />
    );
  }

  const opcoes =
    pergunta.tipo === 'simnao'
      ? [
          { valor: 'sim', rotulo: 'Sim' },
          { valor: 'nao', rotulo: 'Não' },
        ]
      : (pergunta.opcoes ?? []);

  return (
    <Box>
      <Typography sx={{ mb: 1 }}>{pergunta.texto}</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={resposta?.valor ?? null}
        onChange={(_e, valor: string | null) => {
          if (valor) onChange(valor, valor === 'sim' ? resposta?.detalhe : undefined);
        }}
        sx={{ flexWrap: 'wrap' }}
      >
        {opcoes.map((o) => (
          <ToggleButton key={o.valor} value={o.valor} sx={{ px: 2.5, textTransform: 'none' }}>
            {o.rotulo}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {pergunta.tipo === 'simnao' && pergunta.detalhe && resposta?.valor === 'sim' && (
        <TextField
          label={pergunta.detalhe}
          value={resposta.detalhe ?? ''}
          onChange={(e) => onChange('sim', e.target.value)}
          fullWidth
          size="small"
          sx={{ mt: 1.5 }}
        />
      )}
    </Box>
  );
};

export const ResponderAnamnesePage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [busca] = useSearchParams();
  const noTablet = busca.get('tablet') === '1';

  const [resumo, setResumo] = useState<ResumoPublico | null>(null);
  const [erroInicial, setErroInicial] = useState<string | null>(null);
  const [cpf, setCpf] = useState('');
  const [questionario, setQuestionario] = useState<QuestionarioPublico | null>(null);
  const [respostas, setRespostas] = useState<Respostas>({});
  const [aceite, setAceite] = useState(false);
  const [imagem, setImagem] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    anamneseDigitalService
      .resumoPublico(token)
      .then(setResumo)
      .catch((err: unknown) => setErroInicial(err instanceof Error ? err.message : 'Link inválido ou já utilizado.'));
  }, [token]);

  const confirmarCpf = useCallback(async () => {
    setErro(null);
    setOcupado(true);
    try {
      setQuestionario(await anamneseDigitalService.confirmarCpf(token, cpf));
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível conferir o CPF.');
    } finally {
      setOcupado(false);
    }
  }, [token, cpf]);

  const faltando = useMemo(() => {
    if (!questionario) return [];
    return questionario.questionario
      .flatMap((s) => s.perguntas)
      .filter((p) => p.tipo !== 'texto' && !respostas[p.id]?.valor);
  }, [questionario, respostas]);

  const enviar = useCallback(async () => {
    if (!imagem) return;
    setErro(null);
    setOcupado(true);
    try {
      await anamneseDigitalService.responder(token, { cpf, respostas, aceite, imagem, origem: noTablet ? 'tablet' : 'link' });
      setEnviado(true);
      window.scrollTo({ top: 0 });
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível enviar o questionário.');
    } finally {
      setOcupado(false);
    }
  }, [token, cpf, respostas, aceite, imagem, noTablet]);

  if (erroInicial) {
    return (
      <Moldura>
        <Typography variant="h5" gutterBottom>
          Questionário de saúde
        </Typography>
        <Alert severity="warning">{erroInicial}</Alert>
      </Moldura>
    );
  }

  if (!resumo) {
    return (
      <Moldura>
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      </Moldura>
    );
  }

  if (enviado) {
    return (
      <Moldura>
        <Stack alignItems="center" spacing={2} sx={{ py: 4, textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 56 }} />
          <Typography variant="h5">Questionário enviado</Typography>
          <Typography color="text.secondary">
            Obrigado! O médico vai ver as suas respostas na consulta.
            {noTablet ? ' Pode devolver o tablet à recepção.' : ' Você já pode fechar esta página.'}
          </Typography>
        </Stack>
      </Moldura>
    );
  }

  if (resumo.status !== 'pendente') {
    const texto =
      resumo.status === 'expirada'
        ? 'O prazo deste link venceu. Peça um novo à empresa ou à recepção da clínica.'
        : 'Este questionário já foi respondido ou não está mais aberto.';
    return (
      <Moldura>
        <Typography variant="h5" gutterBottom>
          Questionário de saúde
        </Typography>
        <Alert severity="info">{texto}</Alert>
      </Moldura>
    );
  }

  return (
    <Moldura>
      <Typography variant="h5" gutterBottom>
        Questionário de saúde
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {resumo.trabalhador}, antes do seu exame ocupacional de {dataBr(resumo.dataDoExame)}
        {resumo.empresa ? ` (${resumo.empresa})` : ''}, responda algumas perguntas sobre a sua saúde. Leva uns 5 minutos,
        e só o médico vê as respostas.
      </Typography>

      {!questionario ? (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
            Para começar, confirme o seu CPF
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              label="Seu CPF"
              value={cpf}
              onChange={(e) => setCpf(formatarCpf(e.target.value))}
              autoComplete="off"
              size="small"
              fullWidth
              inputProps={{ inputMode: 'numeric' }}
            />
            <Button
              variant="contained"
              onClick={() => void confirmarCpf()}
              disabled={ocupado || cpf.replace(/\D/g, '').length !== 11}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Continuar
            </Button>
          </Stack>
          {erro && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {erro}
            </Alert>
          )}
        </Box>
      ) : (
        <Box>
          {questionario.questionario.map((secao) => (
            <Box key={secao.id} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                {secao.titulo}
              </Typography>
              <Stack spacing={2.5}>
                {secao.perguntas.map((pergunta) => (
                  <CampoDaPergunta
                    key={pergunta.id}
                    pergunta={pergunta}
                    resposta={respostas[pergunta.id]}
                    onChange={(valor, detalhe) =>
                      setRespostas((atuais) => ({
                        ...atuais,
                        [pergunta.id]: detalhe === undefined ? { valor } : { valor, detalhe },
                      }))
                    }
                  />
                ))}
              </Stack>
              <Divider sx={{ mt: 3 }} />
            </Box>
          ))}

          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Assinatura
          </Typography>
          <Stack spacing={2}>
            <FormControlLabel
              control={<Checkbox checked={aceite} onChange={(e) => setAceite(e.target.checked)} />}
              label={<Typography variant="body2">{questionario.declaracao}</Typography>}
            />
            <QuadroDeAssinatura onChange={setImagem} />
            {faltando.length > 0 && (
              <Alert severity="info">
                Faltam {faltando.length} {faltando.length === 1 ? 'pergunta' : 'perguntas'} para responder.
              </Alert>
            )}
            {erro && <Alert severity="error">{erro}</Alert>}
            <Button
              variant="contained"
              size="large"
              onClick={() => void enviar()}
              disabled={ocupado || faltando.length > 0 || !aceite || !imagem}
            >
              {ocupado ? 'Enviando…' : 'Enviar questionário'}
            </Button>
          </Stack>
        </Box>
      )}
    </Moldura>
  );
};

export default ResponderAnamnesePage;
